// =============================================================================
// DEBUG CONSOLE WRAPPER
// =============================================================================

/**
 * Namespaced Debug Console System
 * 
 * Provides conditional console logging based on localStorage flags.
 * Supports both global and module-specific debug modes.
 * 
 * ## Usage Examples:
 * 
 * ### Basic Usage (Global Debug):
 * ```javascript
 * import debugConsole from './debug-console.js';
 * 
 * // Enable global debugging in browser console:
 * localStorage.setItem('debug', 'true');
 * 
 * // Now all debug calls will log:
 * debugConsole.log('This will show');
 * debugConsole.warn('This will show'); 
 * debugConsole.error('This always shows');
 * ```
 * 
 * ### Namespaced Usage (Module-Specific Debug):
 * ```javascript
 * import debugConsoleModule from './debug-console.js';
 * 
 * // Create namespaced console for your module
 * const debugConsole = debugConsoleModule.createNamespace('jentis');
 * 
 * // Enable only this module's debugging in browser console:
 * localStorage.setItem('debug-jentis', 'true');
 * 
 * // Now only this module's debug calls will log:
 * debugConsole.log('Jentis: This will show');
 * debugConsole.warn('Jentis: This will show');
 * ```
 * 
 * ### Multiple Modules:
 * ```javascript
 * // Module A
 * const debugA = debugConsoleModule.createNamespace('moduleA');
 * 
 * // Module B  
 * const debugB = debugConsoleModule.createNamespace('moduleB');
 * 
 * // Enable specific modules:
 * localStorage.setItem('debug-moduleA', 'true');  // Only module A logs
 * localStorage.setItem('debug-moduleB', 'true');  // Only module B logs
 * localStorage.setItem('debug', 'true');          // All modules log
 * ```
 * 
 * ## Debug Flags:
 * 
 * | localStorage Key | Value | Effect |
 * |------------------|-------|--------|
 * | `debug` | `'true'` | Enables ALL debug output globally |
 * | `debug-{namespace}` | `'true'` | Enables output for specific namespace only |
 * 
 * ## Browser Console Commands:
 * 
 * ```javascript
 * // Enable global debugging (all modules)
 * localStorage.setItem('debug', 'true');
 * 
 * // Enable specific module debugging
 * localStorage.setItem('debug-jentis', 'true');
 * localStorage.setItem('debug-tacos', 'true');
 * 
 * // Disable debugging
 * localStorage.removeItem('debug');
 * localStorage.removeItem('debug-jentis');
 * 
 * // Check current debug state
 * localStorage.getItem('debug');
 * localStorage.getItem('debug-jentis');
 * 
 * // Manually check what namespaces are enabled
 * debugConsole.logDebugStatus();
 * ```
 * 
 * ## Debug Status Logging:
 * The system automatically logs which namespaces are enabled when:
 * - A namespaced console is created (if any debugging is active)
 * - You manually call `debugConsole.logDebugStatus()`
 * 
 * Example output:
 * ```
 * [DEBUG-CONSOLE] 🌍 Global debug enabled (all modules)
 * [DEBUG-CONSOLE] 🏷️ Namespace debug enabled: jentis, tacos
 * [DEBUG-CONSOLE] 🔇 No debug logging enabled
 * ```
 * 
 * ## Error Handling:
 * - `error()` method always logs regardless of debug flags
 * - Gracefully handles localStorage unavailability
 * - Safe to use in environments without localStorage
 * 
 * @module debug-console
 */

/**
 * Console wrapper that supports global and namespaced debug logging
 */
const debugConsole = {
    /**
     * Gets all currently enabled debug namespaces
     * @returns {Object} Object with enabled namespaces and global debug status
     */
    /* eslint-disable max-lines-per-function */
    getEnabledNamespaces() {
        if (typeof localStorage === 'undefined') return { global: false, namespaces: [] };
        
        try {
            const result = {
                global: localStorage.getItem('debug') === 'true',
                namespaces: []
            };
            
            // Check all localStorage keys for debug-* patterns
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key?.startsWith('debug-') && localStorage.getItem(key) === 'true') {
                    result.namespaces.push(key.replace('debug-', ''));
                }
            }
            
            return result;
        } catch (error) {
            return { global: false, namespaces: [] };
        }
    },
    /* eslint-enable max-lines-per-function */

    /**
     * Logs current debug status and enabled namespaces
     */
    logDebugStatus() {
        const status = this.getEnabledNamespaces();
        const messages = [];
        
        if (status.global) {
            messages.push('🌍 Global debug enabled (all modules)');
        }
        
        if (status.namespaces.length > 0) {
            messages.push(`🏷️  Namespace debug enabled: ${status.namespaces.join(', ')}`);
        }
        
        if (!status.global && status.namespaces.length === 0) {
            messages.push('🔇 No debug logging enabled');
        }
        
        console.log('%c[DEBUG-CONSOLE]', 'color: #666; font-weight: bold', ...messages);
    },

    /**
     * Enables debug logging globally or for a specific namespace
     * @param {string} namespace - Optional namespace to enable (e.g., 'jentis')
     *                           If not provided, enables global debugging
     */
    /* eslint-disable max-lines-per-function */
    enable(namespace) {
        if (typeof localStorage === 'undefined') {
            console.warn('[DEBUG-CONSOLE] localStorage not available, cannot enable debugging');
            return;
        }
        
        try {
            if (namespace) {
                localStorage.setItem(`debug-${namespace}`, 'true');
                console.log(`%c[DEBUG-CONSOLE] ✅ Enabled debugging for namespace: ${namespace}`, 'color: #4CAF50; font-weight: bold');
            } else {
                localStorage.setItem('debug', 'true');
                console.log('%c[DEBUG-CONSOLE] ✅ Enabled global debugging (all modules)', 'color: #4CAF50; font-weight: bold');
            }
            
            // Log updated status
            this.logDebugStatus();
        } catch (error) {
            console.error('[DEBUG-CONSOLE] Failed to enable debugging:', error);
        }
    },
    /* eslint-enable max-lines-per-function */

    /**
     * Disables debug logging globally or for a specific namespace
     * @param {string} namespace - Optional namespace to disable (e.g., 'jentis')
     *                           If not provided, disables global debugging
     *                           Special value 'all' disables all debugging
     */
    /* eslint-disable max-lines-per-function */
    disable(namespace) {
        if (typeof localStorage === 'undefined') {
            console.warn('[DEBUG-CONSOLE] localStorage not available, cannot disable debugging');
            return;
        }
        
        try {
            if (namespace === 'all') {
                // Disable all debugging
                const debugKeys = Object.keys(localStorage).filter(key => key.startsWith('debug'));
                debugKeys.forEach(key => localStorage.removeItem(key));
                console.log('%c[DEBUG-CONSOLE] 🔇 Disabled all debugging', 'color: #FF9800; font-weight: bold');
            } else if (namespace) {
                localStorage.removeItem(`debug-${namespace}`);
                console.log(`%c[DEBUG-CONSOLE] 🔇 Disabled debugging for namespace: ${namespace}`, 'color: #FF9800; font-weight: bold');
            } else {
                localStorage.removeItem('debug');
                console.log('%c[DEBUG-CONSOLE] 🔇 Disabled global debugging', 'color: #FF9800; font-weight: bold');
            }
            
            // Log updated status
            this.logDebugStatus();
        } catch (error) {
            console.error('[DEBUG-CONSOLE] Failed to disable debugging:', error);
        }
    },
    /* eslint-enable max-lines-per-function */

    /**
     * Checks if debug mode is enabled for a specific namespace
     * @param {string} namespace - Optional namespace to check (e.g., 'jentis')
     * @returns {boolean} True if debug mode is enabled
     */
    isDebugEnabled(namespace) {
        if (typeof localStorage === 'undefined') return false;
        try {
            // Check global debug flag
            if (localStorage.getItem('debug') === 'true') {
                return true;
            }
            // Check namespace-specific debug flag
            if (namespace && localStorage.getItem(`debug-${namespace}`) === 'true') {
                return true;
            }
            return false;
        } catch (error) {
            return false;
        }
    },

    /**
     * Creates a namespaced debug console
     * @param {string} namespace - Namespace for debug logging
     * @returns {Object} Namespaced debug console
     */
    createNamespace(namespace) {
        // Log debug status when namespace is created (only if any debugging is enabled)
        const status = this.getEnabledNamespaces();
        if (status.global || status.namespaces.length > 0) {
            this.logDebugStatus();
        }
        
        return {
            log: (...args) => this.log(...args, namespace),
            warn: (...args) => this.warn(...args, namespace),
            error: (...args) => this.error(...args, namespace)
        };
    },

    /**
     * Debug console.log wrapper
     * @param {...any} args - Arguments to log
     * @param {string} namespace - Optional namespace (should be last parameter)
     */
    log(...args) {
        const namespace = typeof args[args.length - 1] === 'string' ? args.pop() : null;
        if (this.isDebugEnabled(namespace)) {
            console.log(...args);
        }
    },

    /**
     * Debug console.warn wrapper
     * @param {...any} args - Arguments to warn
     * @param {string} namespace - Optional namespace (should be last parameter)
     */
    warn(...args) {
        const namespace = typeof args[args.length - 1] === 'string' ? args.pop() : null;
        if (this.isDebugEnabled(namespace)) {
            console.warn(...args);
        }
    },

    /**
     * Debug console.error wrapper (always shows errors regardless of debug mode)
     * @param {...any} args - Arguments to error
     */
    error(...args) {
        console.error(...args);
    }
};

export default debugConsole;
